import { createFileRoute } from "@tanstack/react-router"

import { blogPostFrontmatterSchema, postModules } from "@/blog/content"
import { Breadcrumb, CtaBand } from "@/brand/components/landing"
import { HeroBand } from "@/brand/components/landing/page-parts"
import { TRIAL_DAYS } from "@/brand/plan-numbers"
import { breadcrumbJsonLd, buildHead, collectionPageJsonLd } from "@/core/seo"
import { formatDate } from "@/date"
import { getMdxFrontmatters } from "@/mdx"

interface BlogIndexPost {
  slug: string
  title: string
  description: string
  date: string
}

const TITLE = "Blog: notes for Australian sole traders"

/**
 * Head for /blog (T393). An empty blog is a thin page: it stays out of the index until the first
 * post exists. The CollectionPage lists the posts so the card grid and the markup agree.
 */
export const blogIndexSeo = (posts: BlogIndexPost[]) =>
  buildHead({
    title: TITLE,
    description:
      "Short, sourced notes on logbooks, car expenses and ATO record keeping for Australian sole traders, reviewed whenever the rules change.",
    path: "/blog",
    noindex: posts.length === 0,
    jsonLd: [
      breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Blog" }]),
      collectionPageJsonLd({
        name: "Notes for sole traders",
        path: "/blog",
        items: posts.map((p) => ({ name: p.title, path: `/blog/${p.slug}` })),
      }),
    ],
  })

export const Route = createFileRoute("/_brand-layout/blog")({
  loader: () => ({
    posts: getMdxFrontmatters(postModules, blogPostFrontmatterSchema).toSorted(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
    ),
  }),
  head: ({ loaderData }) => {
    const { meta, links, scripts } = blogIndexSeo(loaderData?.posts ?? [])
    return { meta, links, scripts }
  },
  component: BlogIndex,
})

function BlogIndex() {
  const { posts } = Route.useLoaderData()
  return <BlogIndexPage posts={posts} />
}

export function BlogIndexPage({ posts }: { posts: (BlogIndexPost & { imgSrc?: string })[] }) {
  return (
    <>
      <Breadcrumb items={[{ label: "Home", href: "/" }, { label: "Blog" }]} />
      <HeroBand>
        <h1 className="text-3xl font-bold tracking-tight md:text-5xl">Notes for sole traders</h1>
        <p className="text-muted-foreground max-w-[60ch] text-lg">
          Short, sourced guides. Reviewed when the rules change.
        </p>
      </HeroBand>
      <section aria-label="Posts" className="px-band grid gap-4 py-8">
        {posts.length > 0 ? (
          <ul className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {posts.map((post) => (
              <li
                key={post.slug}
                className="border-border bg-card hover:bg-muted/40 has-[a:focus-visible]:ring-ring relative grid content-start gap-3 rounded-xl border p-4 has-[a:focus-visible]:ring-2 forced-colors:has-[a:focus-visible]:outline-2"
              >
                {post.imgSrc && (
                  <img
                    src={post.imgSrc}
                    alt=""
                    loading="lazy"
                    className="bg-muted aspect-video w-full rounded-lg border object-cover"
                  />
                )}
                <time
                  dateTime={new Date(post.date).toISOString()}
                  className="text-muted-foreground text-sm"
                >
                  {formatDate(new Date(post.date))}
                </time>
                <a
                  href={`/blog/${post.slug}`}
                  className="text-lg font-semibold outline-none after:absolute after:inset-0 after:content-['']"
                >
                  {post.title}
                </a>
                <p className="text-muted-foreground text-sm">{post.description}</p>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted-foreground">No posts yet. Check back soon.</p>
        )}
      </section>
      <CtaBand
        title="Put the guides to work"
        description={`${TRIAL_DAYS}-day free trial. No card needed.`}
        ctaLabel="Start free trial"
        ctaHref="/login"
      />
    </>
  )
}
